/**
 * SubstitutionModal — asks the AI for 3 realistic swaps for one shopping
 * list row so operators can dodge allergies, dietary restrictions, or
 * seasonal price spikes without redoing the whole plan.
 *
 * Usage:
 *   <SubstitutionModal
 *     row={rowOrNull}                       // shopping_list[i] or null
 *     onClose={() => setRow(null)}
 *     onApply={(rowIndex, swap) => …}       // caller mutates plan
 *   />
 *
 * The parent owns the plan state; this component only fetches suggestions
 * and hands the chosen swap back via `onApply`.
 */

import React, { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../../App';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  X, Shuffle, Sparkles, ArrowRightLeft, AlertTriangle, Loader2,
} from 'lucide-react';
import AIProgressIndicator from './AIProgressIndicator';

export default function SubstitutionModal({ row, onClose, onApply }) {
  const { API } = useContext(AuthContext);
  const [constraint, setConstraint] = useState('');
  const [loading, setLoading]       = useState(false);
  const [swaps, setSwaps]           = useState([]);
  const [error, setError]           = useState('');

  // Reset when a new row opens.
  useEffect(() => {
    setConstraint('');
    setSwaps([]);
    setError('');
    setLoading(false);
  }, [row?.ingredient, row?.__rowIndex]);

  if (!row) return null;

  const fetchSwaps = async () => {
    setLoading(true);
    setError('');
    setSwaps([]);
    try {
      const { data } = await axios.post(`${API}/tools/meal-plan-ai/suggest-swaps`, {
        ingredient:  row.ingredient,
        quantity_kg: Number(row.quantity_kg) || 0,
        constraint,
      });
      setSwaps(data?.swaps || []);
      if (!data?.swaps?.length) setError('AI returned no swap suggestions — try adding a constraint.');
    } catch (e) {
      setError(e?.response?.data?.detail || 'Swap suggestion failed');
    } finally { setLoading(false); }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 no-print"
      onClick={onClose}
      data-testid="fp-swap-modal"
    >
      <Card
        className="max-w-2xl w-full max-h-[90vh] overflow-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <CardHeader className="flex flex-row items-start justify-between gap-2 space-y-0 sticky top-0 bg-card z-10 border-b border-border">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <Shuffle className="w-4 h-4 text-primary" /> Suggest swaps
            </CardTitle>
            <p className="text-xs text-muted-foreground mt-0.5">
              Swap <span className="font-semibold text-foreground">{row.ingredient}</span>
              {row.quantity_kg ? ` (${Number(row.quantity_kg).toFixed(1)} kg)` : ''} for something similar.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground"
            aria-label="Close"
            data-testid="fp-swap-close"
          >
            <X className="w-4 h-4" />
          </button>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label className="text-xs" htmlFor="fp-swap-constraint">
              Constraint (optional) — allergy, diet, season, budget…
            </Label>
            <div className="flex gap-2">
              <Input
                id="fp-swap-constraint"
                value={constraint}
                onChange={(e) => setConstraint(e.target.value)}
                placeholder="e.g. peanut allergy, vegan, seasonal price spike"
                data-testid="fp-swap-constraint"
              />
              <Button
                type="button"
                onClick={fetchSwaps}
                disabled={loading}
                data-testid="fp-swap-generate"
              >
                {loading
                  ? <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  : <Sparkles className="w-4 h-4 mr-1" />}
                Get swaps
              </Button>
            </div>
          </div>

          <AIProgressIndicator
            active={loading}
            estimatedMs={9000}
            data-testid="fp-swap-progress"
            steps={[
              { at: 0,    label: 'Scanning freeze-dry-safe alternatives…' },
              { at: 3000, label: 'Pricing swaps against Walmart baseline…' },
              { at: 6500, label: 'Weighing risks + freeze-dry notes…' },
            ]}
          />

          {error && (
            <p className="text-xs text-amber-600 flex items-start gap-1" data-testid="fp-swap-error">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              {error}
            </p>
          )}

          {swaps.map((sw, i) => {
            const originalCost = Number(row.raw_cost_usd) || 0;
            const delta = (sw.raw_cost_usd || 0) - originalCost;
            const cheaper = delta < 0;
            return (
              <div
                key={i}
                className="rounded-md border border-border p-3 space-y-2 hover:border-primary/60 transition-colors"
                data-testid={`fp-swap-${i}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="font-semibold text-sm">{sw.ingredient}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {sw.reason}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-sm font-mono font-bold">${Number(sw.raw_cost_usd).toFixed(2)}</div>
                    {originalCost > 0 && (
                      <div
                        className={`text-[10px] font-semibold ${cheaper ? 'text-green-600' : 'text-amber-600'}`}
                      >
                        {cheaper ? '−' : '+'}${Math.abs(delta).toFixed(2)}
                      </div>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2 text-[11px] text-muted-foreground">
                  <div>
                    <span className="font-semibold uppercase tracking-wider text-[9px]">Qty:</span>{' '}
                    {Number(sw.quantity_kg).toFixed(2)} kg · ${Number(sw.price_per_kg).toFixed(2)}/kg
                  </div>
                  {sw.risk && (
                    <div className="flex items-start gap-1">
                      <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0 text-amber-600" />
                      <span>{sw.risk}</span>
                    </div>
                  )}
                </div>
                {sw.freeze_dry_notes && (
                  <div className="text-[11px] italic text-muted-foreground">
                    Freeze-dry: {sw.freeze_dry_notes}
                  </div>
                )}
                <div className="flex justify-end">
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => {
                      onApply(sw);
                      toast.success(`Swapped in ${sw.ingredient}`);
                    }}
                    data-testid={`fp-swap-apply-${i}`}
                  >
                    <ArrowRightLeft className="w-3.5 h-3.5 mr-1" /> Apply swap
                  </Button>
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>
    </div>
  );
}
